<template>
    <div class="rps-match-selection-modal">
        <div v-if="steps.length > 1" class="rps-steps-indicator">
            <div
                v-for="(step, index) in steps"
                :key="index"
                class="rps-step"
                :class="{
                    'rps-step--active': index === currentStep,
                    'rps-step--completed': resolvedSteps[index] !== undefined,
                }"
                @click="goToStep(index)"
            >
                <div class="rps-step__circle">
                    <i v-if="resolvedSteps[index] !== undefined" class="pi pi-check"></i>
                    <span v-else>{{ index + 1 }}</span>
                </div>
                <span class="rps-step__label">{{ step.personName }}</span>
            </div>
        </div>

        <div class="rps-current-person">
            <p class="rps-current-person__heading">
                {{ $t('§eq_operator.rps_select_match_for') }}
                <strong>{{ currentStepData.personName }}</strong>
            </p>
        </div>

        <DataTable
            :value="currentStepData.candidates"
            v-model:selection="selectedCandidate"
            selectionMode="single"
            dataKey="index"
            class="rps-candidates-table"
        >
            <Column selectionMode="single" headerStyle="width: 3rem"></Column>
            <Column
                field="fullName"
                :header="$t('§eq_operator.rps_candidate_name')"
            ></Column>
            <Column
                field="birthDate"
                :header="$t('§eq_operator.rps_candidate_dob')"
            >
                <template #body="slotProps">
                    <template v-if="slotProps.data.birthDate">{{ slotProps.data.birthDate }}</template>
                    <CoreValueMissing v-else />
                </template>
            </Column>
            <Column
                field="countryOfResidence"
                :header="$t('§eq_operator.rps_candidate_country')"
            >
                <template #body="slotProps">
                    <template v-if="slotProps.data.countryOfResidence">{{ slotProps.data.countryOfResidence }}</template>
                    <CoreValueMissing v-else />
                </template>
            </Column>
            <Column
                field="citizenship"
                :header="$t('§eq_operator.rps_candidate_citizenship')"
            >
                <template #body="slotProps">
                    <template v-if="slotProps.data.citizenship">{{ slotProps.data.citizenship }}</template>
                    <CoreValueMissing v-else />
                </template>
            </Column>
        </DataTable>

        <div class="flex justify-start gap-2 mt-4">
            <CoreButton
                :label="isLastStep ? '§ui_button.confirm' : '§ui_button.confirm_and_next'"
                variant="primary"
                :disabled="!selectedCandidate || selectionUnchanged || loading"
                :loading="loading"
                @click="confirmSelection"
            />
            <CoreButton
                :label="isLastStep ? '§ui_button.skip_and_close' : '§ui_button.skip'"
                variant="tertiary"
                :disabled="loading"
                @click="skipStep"
            />
        </div>
    </div>
</template>

<script>
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreValueMissing from '@/components/core/CoreValueMissing.vue'
import { useModalStore } from '@/stores/modal'
import { useAuthorizedPersonStore } from '@/stores/authorizedPerson'

export default {
    name: 'RpsMatchSelectionModal',

    components: {
        DataTable,
        Column,
        CoreButton,
        CoreValueMissing,
    },

    data() {
        return {
            modalStore: useModalStore(),
            authorizedPersonStore: useAuthorizedPersonStore(),
            steps: [],
            currentStep: 0,
            selectedCandidate: null,
            resolvedSteps: {},
            loading: false,
        }
    },

    computed: {
        currentStepData() {
            return this.steps[this.currentStep] || {}
        },

        isLastStep() {
            return this.currentStep >= this.steps.length - 1
        },

        selectionUnchanged() {
            const resolved = this.resolvedSteps[this.currentStep]
            if (resolved === undefined) return false
            return this.selectedCandidate?.index === resolved
        },
    },

    mounted() {
        this.steps = this.modalStore.data.steps || []
    },

    methods: {
        async confirmSelection() {
            if (!this.selectedCandidate || this.loading) return
            this.loading = true

            try {
                const result = await this.authorizedPersonStore.resolveRpsMatch(
                    this.currentStepData.personId,
                    this.selectedCandidate.index
                )
                this.resolvedSteps[this.currentStep] = this.selectedCandidate.index
                if (this.modalStore.data.onResolved) {
                    this.modalStore.data.onResolved(result.id, result.rps)
                }
                this.goToNextStep()
            } catch (error) {
                console.error('Error resolving RPS match:', error)
            } finally {
                this.loading = false
            }
        },

        skipStep() {
            this.goToNextStep()
        },

        goToStep(index) {
            if (this.loading || index === this.currentStep) return
            this.currentStep = index
            this.restoreSelection()
        },

        goToNextStep() {
            if (this.isLastStep) {
                this.modalStore.setClose()
                return
            }
            this.currentStep++
            this.restoreSelection()
        },

        restoreSelection() {
            const resolved = this.resolvedSteps[this.currentStep]
            if (resolved !== undefined) {
                this.selectedCandidate = this.currentStepData.candidates.find(
                    (c) => c.index === resolved
                ) || null
            } else {
                this.selectedCandidate = null
            }
        },
    },
}
</script>

<style>
.rps-match-selection-modal {
    .rps-steps-indicator {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 20px;
        padding: 12px 16px;
        background: var(--color-neutral-100);
        border-radius: 8px;
    }

    .rps-step {
        display: flex;
        align-items: center;
        gap: 6px;
        flex: 1;
        cursor: pointer;
    }

    .rps-step__circle {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.2rem;
        font-weight: 600;
        background: var(--color-neutral-400);
        color: var(--color-neutral-800);
        flex-shrink: 0;
    }

    .rps-step--active .rps-step__circle {
        background: var(--color-primary-700);
        color: var(--color-white);
    }

    .rps-step--completed .rps-step__circle {
        background: var(--color-primary-700);
        color: var(--color-white);
    }

    .rps-step__label {
        font-size: 1.2rem;
        color: var(--color-neutral-700);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .rps-step--active .rps-step__label {
        color: var(--text-dark);
        font-weight: 600;
    }

    .rps-current-person {
        margin-bottom: 12px;
    }

    .rps-current-person__heading {
        font-size: 1.4rem;
        color: var(--text-dark);
    }

    .rps-candidates-table {
        thead th {
            border-bottom: none;
        }

        .p-datatable-tbody > tr {
            cursor: pointer;
        }

        .p-datatable-tbody > tr > td {
            border-bottom: 1px solid var(--color-neutral);
        }

        .p-datatable-tbody > tr.p-highlight {
            background: var(--color-primary-700);
            color: var(--text-dark);
        }

        .p-datatable-tbody > tr.p-highlight > td {
            color: var(--text-dark);
        }

        .p-datatable-tbody > tr:not(.p-highlight):hover {
            background: var(--color-primary-700);
            color: var(--text-dark);
        }

        .p-radiobutton .p-radiobutton-box.p-highlight {
            border-color: var(--color-primary-700);
            background: var(--color-primary-700);
        }

        .p-radiobutton .p-radiobutton-box.p-highlight .p-radiobutton-icon {
            background: var(--color-white);
        }
    }
}
</style>
